// CSVUploadSection.jsx - CSV Upload Component for Non-Registered Users
import React, { useState, useContext } from 'react';
import { Box, Typography, Card, CardContent, Button, Alert, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip } from '@mui/material';
import { CloudUpload, Download, Delete, CheckCircle, Error as ErrorIcon, DeleteOutline, InfoOutlined } from '@mui/icons-material';
import { NotificationDataContext } from '../AddEditNotification';
import { useForm, Controller } from 'react-hook-form';
import NavigationButton from './NavigationButton';
import Papa from 'papaparse';
import Swal from 'sweetalert2';

const CSVUploadSection = () => {
    const { notificationData, setNotificationData, initialState, steps, activeStep, setActiveStep, completedSteps, setCompletedSteps } = useContext(NotificationDataContext);
    const [uploadedData, setUploadedData] = useState(notificationData.csvUploadData?.phoneNumbers || []);
    const [fileName, setFileName] = useState(notificationData.csvUploadData?.fileName || '');
    const [parseErrors, setParseErrors] = useState([]);
    const [uploading, setUploading] = useState(false);

    const { handleSubmit, formState: { isDirty } } = useForm({
        mode: 'onChange'
    });

    const completeStep = () => {
        console.log('CSV Upload Data:', uploadedData);
        setNotificationData(prev => ({
            ...prev,
            csvUploadData: {
                phoneNumbers: uploadedData,
                fileName: fileName
            }
        }));
        
        if (activeStep < steps.length - 1) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
            setCompletedSteps((prev) => {
                prev.add(activeStep);
                return new Set(prev);
            });
        }
    };

    const validatePhoneNumber = (phone) => {
        // Remove all non-digit characters
        const cleaned = String(phone).replace(/\D/g, '');
        
        // Check if it's exactly 10 digits (Indian mobile number)
        if (cleaned.length === 10) {
            // Add 91 country code prefix
            return '91' + cleaned;
        } 
        // If already has 91 prefix (12 digits)
        else if (cleaned.length === 12 && cleaned.startsWith('91')) {
            return cleaned;
        } 
        // If has 091 prefix (13 digits)
        else if (cleaned.length === 13 && cleaned.startsWith('091')) {
            // Remove leading 0 and return
            return cleaned.substring(1);
        }
        
        return null;
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploading(true);
        setParseErrors([]);
        setFileName(file.name);

        Papa.parse(file, {
            complete: (results) => {
                console.log('Parsed CSV:', results);
                const phoneNumbers = [];
                const errors = [];

                results.data.forEach((row, index) => {
                    // Handle different CSV formats
                    // Support columns: phone, phoneNumber, mobileNumber, mobile, or first column
                    let phoneValue = row.phone || row.phoneNumber || row.mobileNumber || row.mobile || row[0];
                    
                    if (phoneValue) {
                        const validated = validatePhoneNumber(phoneValue);
                        if (validated) {
                            // Avoid duplicates
                            if (!phoneNumbers.includes(validated)) {
                                phoneNumbers.push(validated);
                            }
                        } else {
                            errors.push(`Row ${index + 1}: Invalid phone number format - ${phoneValue}`);
                        }
                    }
                });

                setUploadedData(phoneNumbers);
                setParseErrors(errors);
                setUploading(false);

                // Auto-save to notification data
                setNotificationData(prev => ({
                    ...prev,
                    csvUploadData: {
                        phoneNumbers: phoneNumbers,
                        fileName: file.name
                    }
                }));
            },
            header: true,
            skipEmptyLines: true,
            error: (error) => {
                console.error('CSV Parse Error:', error);
                setParseErrors([`Failed to parse CSV: ${error.message}`]);
                setUploading(false);
            }
        });
    };

    const handleClearData = () => {
        setUploadedData([]);
        setFileName('');
        setParseErrors([]);
        setNotificationData(prev => ({
            ...prev,
            csvUploadData: initialState.csvUploadData
        }));
    };

    const handleDeleteNumber = (indexToDelete) => {
        const updatedNumbers = uploadedData.filter((_, index) => index !== indexToDelete);
        setUploadedData(updatedNumbers);
        
        // If all numbers are deleted, clear the file name and errors too
        if (updatedNumbers.length === 0) {
            setFileName('');
            setParseErrors([]);
            setNotificationData(prev => ({
                ...prev,
                csvUploadData: initialState.csvUploadData
            }));
        } else {
            // Update notification data
            setNotificationData(prev => ({
                ...prev,
                csvUploadData: {
                    phoneNumbers: updatedNumbers,
                    fileName: fileName
                }
            }));
        }
    };

    const downloadSampleCSV = () => {
        const sampleData = 'phoneNumber\n9876543210\n9876543211\n9876543212\n9123456789\n9998887776';
        const blob = new Blob([sampleData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sample_phone_numbers.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const showInstructions = () => {
        Swal.fire({
            title: '<strong style="font-size: 1rem;">CSV Upload Instructions</strong>',
            // icon: 'info',
            // iconHtml: '<i class="swal2-icon swal2-info" style="font-size: 2rem;"></i>',
            html: `
                <div style="text-align: left;">
                    <p style="font-size: 0.8rem; margin-bottom: 10px;"><strong>📱 Purpose:</strong> <span style="font-size: 0.75rem;">Send WhatsApp messages to non-registered users</span></p>
                    
                    <p style="font-size: 0.8rem; margin-bottom: 5px;"><strong>📄 File Format:</strong></p>
                    <ul style="margin-left: 20px; font-size: 0.75rem;">
                        <li>CSV file with phone numbers</li>
                        <li>Column name: 'phoneNumber', 'phone', 'mobile', or 'mobileNumber'</li>
                    </ul>
                    
                    <p style="font-size: 0.8rem; margin-bottom: 5px; margin-top: 10px;"><strong>✅ Requirements:</strong></p>
                    <ul style="margin-left: 20px; font-size: 0.75rem;">
                        <li>Phone numbers must be exactly <strong>10 digits</strong></li>
                        <li>System auto-adds 91 country code</li>
                        <li>Example: 9876543210 → 919876543210</li>
                    </ul>
                    
                    <p style="font-size: 0.8rem; margin-bottom: 5px; margin-top: 10px;"><strong>💡 Tips:</strong></p>
                    <ul style="margin-left: 20px; font-size: 0.75rem;">
                        <li>Download sample CSV for reference</li>
                        <li>Duplicates are automatically removed</li>
                        <li>Invalid numbers will be shown in errors</li>
                    </ul>
                </div>
            `,
            showCloseButton: true,
            confirmButtonText: 'Got it!',
            confirmButtonColor: '#1976d2',
            customClass: {
                title: 'swal-title-custom',
                htmlContainer: 'swal-html-custom',
                confirmButton: 'swal-button-small',
                icon: 'swal-icon-small'
            },
            buttonsStyling: true,
            didOpen: () => {
                // Custom styling for button and icon
                const confirmButton = Swal.getConfirmButton();
                if (confirmButton) {
                    confirmButton.style.fontSize = '0.75rem';
                    confirmButton.style.padding = '6px 16px';
                }
                const icon = Swal.getIcon();
                if (icon) {
                    icon.style.width = '50px';
                    icon.style.height = '50px';
                    icon.style.fontSize = '2rem';
                }
            }
        });
    };

    return (
        <Box component={'form'} onSubmit={handleSubmit(completeStep)}>
            <Card>
                <CardContent sx={{ height: 'max-content',height:'55vh', overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                            Upload Phone Numbers (CSV)
                            <Tooltip title="View Instructions">
                                {/* <IconButton 
                                    size="small" 
                                    onClick={showInstructions}
                                    sx={{ 
                                        bgcolor: 'primary.50',
                                        '&:hover': { bgcolor: 'primary.100' }
                                    }}
                                >
                                    <InfoOutlined fontSize="small" color="primary" />
                                </IconButton> */}
                                <Typography
                                    onClick={showInstructions}
                                    sx={{
                                        fontSize: '0.8rem',
                                        color: 'primary.main',
                                        fontWeight: 500,
                                        // textDecoration: 'underline',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            color: 'primary.dark'
                                        }
                                    }}
                                >
                                    {/* <InfoOutlined fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} /> */}
                                    View Instructions <Box component="span" sx={{ fontSize: '1rem', fontWeight: 400,color: 'error.main' }}>*</Box>
                                </Typography>
                            </Tooltip>
                        </Typography>
                        {/* Parse Errors */}
                    {parseErrors.length > 0 && (
                        <Paper sx={{ mb: 2,bgcolor:'#fff4e5',height:'100%',display:'flex',alignItems:'center' }}>
                            <Typography sx={{ fontWeight: 600,fontSize:".75rem",p:1,color:'#663c00'}}>
                                Found {parseErrors.length} invalid entries
                            </Typography>
                            {/* <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 150, overflow: 'auto' }}>
                                {parseErrors.slice(0, 10).map((error, index) => (
                                    <li key={index}>
                                        <Typography variant="caption">{error}</Typography>
                                    </li>
                                ))}
                                {parseErrors.length > 10 && (
                                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                        ...and {parseErrors.length - 10} more
                                    </Typography>
                                )}
                            </Box> */}
                        </Paper>
                    )}
                    </Box>

                    <Box sx={{ display: 'flex' ,justifyContent:'space-between',mb:1}}>
                        <Box sx={{display:'flex',gap:2}}>
                            <Button
                                variant="outlined"
                                startIcon={<Download />}
                                onClick={downloadSampleCSV}
                                size="small"
                            >
                                Download Sample CSV
                            </Button>

                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<CloudUpload />}
                                disabled={uploading}
                            >
                                Upload CSV File
                                <input
                                    type="file"
                                    hidden
                                    accept=".csv"
                                    onChange={handleFileUpload}
                                />
                            </Button>

                            {uploadedData.length > 0 && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<Delete />}
                                    onClick={handleClearData}
                                    size="small"
                                >
                                    Clear Data
                                </Button>
                            )}
                            
                        </Box>
                            {/* Upload Summary */}
                            {fileName && (
                                <Paper variant="outlined" sx={{ bgcolor: 'background.default',width:'45%' }}>
                                    {/* <CardContent> */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',alignItems:'center' ,height:'100%'}}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CheckCircle color="success" />
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    File: {fileName}
                                                </Typography>
                                            </Box>
                                            <Chip 
                                                label={`${uploadedData.length} Valid Numbers`} 
                                                color="success" 
                                                size="small" 
                                            />
                                        </Box>
                                    {/* </CardContent> */}
                                </Paper>
                            )}
                    </Box>

                    {/* Phone Numbers Preview */}
                    {uploadedData.length > 0 && (
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Uploaded Phone Numbers ({uploadedData.length})
                                </Typography>
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        flexWrap: 'wrap', 
                                        gap: 1, 
                                        maxHeight: 300, 
                                        overflow: 'auto',
                                        p: 1,
                                        bgcolor: 'grey.50',
                                        borderRadius: 1
                                    }}
                                >
                                    {uploadedData.map((phone, index) => (
                                        <Chip
                                            key={index}
                                            label={phone}
                                            onDelete={() => handleDeleteNumber(index)}
                                            // color="error"
                                            variant="outlined"
                                            sx={{
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'error.main',
                                                    '&:hover': {
                                                        color: 'error.dark'
                                                    }
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Validation Message */}
                    {uploadedData.length === 0 && !uploading && (
                        <Alert severity="warning">
                            Please upload a CSV file with phone numbers to proceed.
                        </Alert>
                    )}
                </CardContent>
            </Card>

            <NavigationButton isDirty={uploadedData.length > 0} />
        </Box>
    );
};

export default CSVUploadSection;
